/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { AriesCardsSectionFragment } from "@cosmos/hygraph-schema";

import {
	ResponsiveContainer,
	SectionHeading,
	BackgroundContainer,
	SectionContainer,
	hasContent,
} from "@components/elements";
import { HeadingLevelContext, useCardHeadingOffset } from "@components/context";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import { Flex, VStack } from "@panda/jsx";

import { AriesCard } from "./AriesCard.molecule";

export const AriesCardsSection = ({ config, cards }: AriesCardsSectionFragment): React.JSX.Element | null => {
	const cardOffset = useCardHeadingOffset(hasContent(config?.heading?.title));
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(cards?.length);
	if (!validHeading && !validContent) return null;
	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)}>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)}>
					<VStack alignItems="stretch" gap="eds.dimension.48">
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							// Cards render h3. Shift them up one level (h3→h2, floored) when there's no
							// section title, OR when the title has been promoted to the page h1 (first
							// hero-less section), so the first heading isn't an h1/h2→h3 skip.
							<HeadingLevelContext.Provider value={{ offset: cardOffset }}>
								<Flex gap="eds.dimension.16" flexWrap="wrap">
									{(cards || []).map((card) => (
										<AriesCard key={`aries-card-${card.id}`} {...card}></AriesCard>
									))}
								</Flex>
							</HeadingLevelContext.Provider>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
